import {
    BuildingBlockType,
    DiagramBuildingBlock,
    DiagramTableBuildingBlock,
    Report,
    SerializedDiagramBuildingBlock,
    SerializedDiagramTableBuildingBlock,
    SerializedReport,
    SerializedTableBuildingBlock,
    TableBuildingBlock,
    ToggleableSelectorTableColumnDef,
} from '../types/Report';
import ReportEditorStore from '../stores/ReportEditorStore';
import {AnalyticsTab} from '../../stores/store';
import {ProductKPI, SearchtermKPI} from '../../kpi';
import {TableSortDirection} from '../../types/Table';
import {DataKey} from '../../stores/GeneralSelectorTableStore';

export default class ReportSerializer {
    constructor(private getTargetCurrency: () => string | undefined) {}

    public serializeReport(report: Report): SerializedReport {
        return {
            lastChanged: report.lastChanged,
            layout: report.layout,
            variables: report.variables,
            id: report.id,
            name: report.name,
            blocks: report.blocks.map((buildingBlock) => {
                switch (buildingBlock.blockType) {
                    case BuildingBlockType.TEXT:
                        return buildingBlock;
                    case BuildingBlockType.TABLE:
                        return ReportSerializer.serializeTableBuildingBlock(buildingBlock);
                    case BuildingBlockType.DIAGRAM:
                        return ReportSerializer.serializeDiagramBuildingBlock(buildingBlock);
                    case BuildingBlockType.DIAGRAM_TABLE:
                        return ReportSerializer.serializeDiagramTableBuildingBlock(buildingBlock);
                    case BuildingBlockType.PAGE_BREAK:
                        return buildingBlock;
                    default:
                        throw Error('Serialization of block type not implemented yet');
                }
            }),
            currency: this.getTargetCurrency(),
            dates: report.dates,
        };
    }

    private static serializeTableBuildingBlock(tableBuildingBlock: TableBuildingBlock): SerializedTableBuildingBlock {
        const mergedTableInstruction = ReportEditorStore.mergeTableInstructions(tableBuildingBlock.memoryItems);

        if (!mergedTableInstruction.tableInstruction) {
            throw Error('Could not serialize table building block with id' + tableBuildingBlock.id);
        }

        const activeView = mergedTableInstruction.tableInstruction.activeViews[0];

        const getFirstItemOrDefault = <T>(data: T[], defaultValue: T): T => {
            if (data.length > 0) {
                return data[0];
            }
            return defaultValue;
        };

        const sorting = getFirstItemOrDefault(mergedTableInstruction.tableInstruction.sortings, {
            attribute: activeView === AnalyticsTab.SearchView ? SearchtermKPI.SalesFromSearches : ProductKPI.Sales,
            sortDirection: TableSortDirection.DESC,
        });

        if (!activeView) {
            throw Error('Cannot serialize table building block without analytics tab relation');
        }

        if (mergedTableInstruction.tableInstruction.channels.length === 0) {
            throw Error('Cannot serialize table building block without channel');
        }

        return {
            tableMode: activeView === AnalyticsTab.SearchView ? 'search' : 'product',
            id: tableBuildingBlock.id,
            blockType: tableBuildingBlock.blockType,
            limit: tableBuildingBlock.lineLimit,
            search: getFirstItemOrDefault(mergedTableInstruction.tableInstruction.searches, ''),
            sortAttribute: sorting.attribute,
            sortDirection: sorting.sortDirection,
            filters: mergedTableInstruction.tableInstruction.filters,
            channel: mergedTableInstruction.tableInstruction.channels[0],
            columns: ReportSerializer.serializeToggleablSelectorTableColumnDef(tableBuildingBlock.columns),
            idDate: tableBuildingBlock.idDate,
        };
    }

    private static serializeDiagramBuildingBlock(diagramBuildingBlock: DiagramBuildingBlock): SerializedDiagramBuildingBlock {
        const mergedDiagramData = ReportEditorStore.mergeDiagramInstructions(diagramBuildingBlock.memoryItems);
        return {
            id: diagramBuildingBlock.id,
            blockType: diagramBuildingBlock.blockType,
            productDiagrams: mergedDiagramData.diagramInstruction ? mergedDiagramData.diagramInstruction.productDiagrams : [],
            searchDiagrams: mergedDiagramData.diagramInstruction ? mergedDiagramData.diagramInstruction.searchDiagrams : [],
            idDate: diagramBuildingBlock.idDate,
            idDateCompare: diagramBuildingBlock.idDateCompare,
        };
    }

    private static serializeDiagramTableBuildingBlock(diagramTableBuildingBlock: DiagramTableBuildingBlock): SerializedDiagramTableBuildingBlock {
        const mergedDiagramData = ReportEditorStore.mergeDiagramInstructions(diagramTableBuildingBlock.memoryItems);
        return {
            id: diagramTableBuildingBlock.id,
            blockType: diagramTableBuildingBlock.blockType,
            productDiagrams: mergedDiagramData.diagramInstruction ? mergedDiagramData.diagramInstruction.productDiagrams : [],
            searchDiagrams: mergedDiagramData.diagramInstruction ? mergedDiagramData.diagramInstruction.searchDiagrams : [],
            columns: ReportSerializer.serializeToggleablSelectorTableColumnDef(diagramTableBuildingBlock.columns),
            idDate: diagramTableBuildingBlock.idDate,
            idDateCompare: diagramTableBuildingBlock.idDateCompare,
        };
    }

    private static serializeToggleablSelectorTableColumnDef(columns: ToggleableSelectorTableColumnDef[]): DataKey[] {
        return columns.reduce((visibleDataKeys: DataKey[], column) => {
            if (column.isVisible) {
                visibleDataKeys.push(column.dataKey);
            }
            return visibleDataKeys;
        }, []);
    }
}



// WEBPACK FOOTER //
// ./src/report/utility/ReportSerializer.ts